import api from "@/api/api";
import SupportPreview from "@/views/supports/SupportPreview.vue";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { authGuard } from "./guards";
import { supportRoutes } from "./supports";

// Import direct pour les composants critiques (core layout + auth)
import Login from "@/views/auth/Login.vue";
import Dashboard from "@/views/dashboard/Dashboard.vue";
import Home from "@/views/dashboard/Home.vue";

// Lazy loading pour les composants moins critiques
const ChooseWorkspace = () => import("@/views/dashboard/ChooseWorkspace.vue");
const Profile = () => import("@/views/profile/Profile.vue");
const Documents = () => import("@/views/documents/Documents.vue");
const Extranet = () => import("@/views/dashboard/Extranet.vue");
const ProviderDetails = () => import("@/views/dashboard/ProviderDetails.vue");
const Products = () => import("@/views/orders/Products.vue");
const AGCO = () => import("@/views/dashboard/AGCO.vue");
const ProductPhotos = () =>
  import("@/views/documents/phototheque/ProductPhotos.vue");
const AgcoPhotos = () => import("@/views/documents/phototheque/AgcoPhotos.vue");
const SupplierPhotos = () =>
  import("@/views/documents/phototheque/SupplierPhotos.vue");
const DocumentsAgco = () => import("@/views/documents/DocumentsAgco.vue");

// Lazy loading pour les pages Semaine Promo
const PromoHome = () => import("@/views/promo/PromoHome.vue");
const PromoSupplierPage = () => import("@/views/promo/PromoSupplierPage.vue");

// Forum AGCO (concessionnaires authentifiés) : présentation + inscription
const ForumPresentation = () => import("@/views/forum/ForumPresentation.vue");
const ForumInscriptionTableur = () =>
  import("@/views/forum/InscriptionTableur.vue");

import { orderRoutes } from "./order";

declare module "vue-router" {
  interface RouteMeta {
    requiresAuth?: boolean;
    [key: string]: any;
  }
}

// Configuration de l'URL de base pour api
const apiBaseUrl: string = import.meta.env.VITE_API_URL_FULL;
api.defaults.baseURL = apiBaseUrl;
api.defaults.withCredentials = false;

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    name: "login",
    component: Login,
    meta: { requiresAuth: false },
  },
  {
    path: "/supports/preview/:id",
    name: "support-preview",
    component: SupportPreview,
    meta: { requiresAuth: false, public: true },
  },
  {
    path: "/choose-workspace",
    name: "choose-workspace",
    component: ChooseWorkspace,
    meta: { requiresAuth: true },
  },
  {
    path: "/",
    component: Dashboard,
    meta: { requiresAuth: true },
    children: [
      {
        path: "",
        name: "home",
        component: Home,
      },
      {
        path: "profile",
        name: "profile",
        component: Profile,
      },
      {
        path: "documents",
        name: "documents",
        component: Documents,
      },
      {
        path: "personal-photos",
        name: "personal-photos",
        component: ProductPhotos,
      },
      {
        path: "extranet",
        name: "extranet",
        component: Extranet,
      },
      {
        path: "extranet/provider/:id",
        name: "provider-details",
        component: ProviderDetails,
      },
      {
        path: "products",
        name: "products",
        component: Products,
      },
      {
        path: "agco",
        name: "agco",
        component: AGCO,
      },
      {
        path: "supplier-photos",
        name: "supplier-photos",
        component: SupplierPhotos,
      },
      {
        path: "agco-photos",
        name: "agco-photos",
        component: AgcoPhotos,
      },
      {
        path: "documents-agco",
        name: "documents-agco",
        component: DocumentsAgco,
      },
      {
        path: "agco-documents",
        name: "agco-documents",
        component: DocumentsAgco,
      },
      {
        path: "help",
        name: "help",
        component: () => import("@/views/help/Help.vue"),
      },
      {
        path: "help/:id",
        name: "help-detail",
        component: () => import("@/views/help/HelpDetail.vue"),
        props: true,
      },
      // Routes Semaine Promo
      {
        path: "semaine-promo",
        name: "promo-home",
        component: PromoHome,
      },
      {
        path: "semaine-promo/fournisseurs/:id",
        name: "promo-supplier",
        component: PromoSupplierPage,
        props: true,
      },
      // Forum AGCO (concessionnaires authentifiés)
      {
        path: "forum",
        name: "forum-presentation",
        component: ForumPresentation,
      },
      {
        path: "forum/exposants",
        name: "forum-exhibitors",
        component: () => import("@/views/forum/ForumExhibitors.vue"),
      },
      {
        path: "forum/hotels",
        name: "forum-hotels",
        component: () => import("@/views/forum/ForumHotels.vue"),
      },
      {
        path: "forum/inscription",
        name: "forum-inscription",
        component: ForumInscriptionTableur,
      },
      ...orderRoutes,
      ...supportRoutes,
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  // Gère le défilement vers une ancre (#hash) y compris quand le contenu
  // est chargé de façon asynchrone (ex. section « Se déplacer » des hôtels).
  scrollBehavior(to, _from, savedPosition) {
    if (to.hash) {
      return new Promise((resolve) => {
        setTimeout(() => resolve({ el: to.hash, top: 80, behavior: "smooth" }), 400);
      });
    }
    if (savedPosition) return savedPosition;
    return { top: 0 };
  },
});

// Utiliser uniquement le garde d'authentification
router.beforeEach(authGuard);

// Gérer la redirection après le changement de workspace
router.isReady().then(() => {
  const redirectUrl = localStorage.getItem("redirect_after_brand_change");
  if (redirectUrl) {
    localStorage.removeItem("redirect_after_brand_change");
    router.push(redirectUrl);
  }
});

export default router;
